"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { fetchCourseLearnerState, postCourseApply } from "../lib/courseLearnerApi";

const ANCHOR_ENROLL = "#course-enrollment";
const ANCHOR_CURRICULUM = "#course-curriculum";

/**
 * Primary CTAs only: browse curriculum, submit join (one tap), login, or disabled states.
 * Duplicate apply UI is not rendered in CourseJoinBlock.
 */
export default function CourseJoinNavButtons({ courseId, isOpen, variant = "hero", sidebarTone = "dark" }) {
    const [learner, setLearner] = useState(undefined);
    const [applying, setApplying] = useState(false);

    const reload = useCallback(() => {
        void fetchCourseLearnerState(courseId, []).then(setLearner);
    }, [courseId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        const onReload = () => reload();
        if (typeof window !== "undefined") {
            window.addEventListener("figmawya-course-learner-reload", onReload);
            return () => window.removeEventListener("figmawya-course-learner-reload", onReload);
        }
    }, [reload]);

    const handleApply = async () => {
        setApplying(true);
        try {
            await postCourseApply(courseId, "");
            reload();
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("figmawya-course-learner-reload"));
            }
        } catch (err) {
            window.alert(err?.message || "تعذر إرسال الطلب");
        } finally {
            setApplying(false);
        }
    };

    const canBrowseContent =
        learner != null &&
        (typeof learner.canViewContent === "boolean"
            ? learner.canViewContent === true
            : learner.requiresEnrollment === false);

    const access = learner?.enrollmentAccess ?? "none";

    const heroClass = "btn btn-primary btn-lg px-5 rounded-pill";
    const sidebarClass =
        sidebarTone === "success"
            ? "btn btn-success btn-lg w-100 mb-3 rounded-pill"
            : "btn btn-dark btn-lg w-100 mb-3 rounded-pill";

    const outerClass = variant === "hero" ? heroClass : sidebarClass;

    if (!isOpen) {
        if (variant === "hero") {
            return (
                <button type="button" className={heroClass} disabled>
                    سيفتح قريباً
                </button>
            );
        }
        return (
            <button type="button" className={sidebarClass} disabled>
                سيفتح قريباً
            </button>
        );
    }

    if (learner == null) {
        return (
            <Link href={ANCHOR_ENROLL} className={outerClass} scroll>
                {variant === "sidebar" ? "طلب الانضمام" : "طلب الانضمام للدورة"}
            </Link>
        );
    }

    if (canBrowseContent) {
        return (
            <Link href={ANCHOR_CURRICULUM} className={outerClass} scroll>
                استعرض محتوى الدورة
            </Link>
        );
    }

    if (access === "needs_apply") {
        return (
            <button type="button" className={outerClass} disabled={applying} onClick={() => void handleApply()}>
                {applying ? "جارٍ الإرسال…" : variant === "sidebar" ? "طلب الانضمام" : "طلب الانضمام للدورة"}
            </button>
        );
    }

    if (access === "login_required") {
        return (
            <Link href="/login" className={outerClass}>
                سجّل الدخول لطلب الانضمام
            </Link>
        );
    }

    if (access === "pending") {
        return (
            <button type="button" className={outerClass} disabled>
                قيد المراجعة
            </button>
        );
    }

    if (access === "refused") {
        return (
            <button type="button" className={outerClass} disabled>
                تم رفض الطلب
            </button>
        );
    }

    return (
        <Link href={ANCHOR_ENROLL} className={outerClass} scroll>
            {variant === "sidebar" ? "طلب الانضمام" : "طلب الانضمام للدورة"}
        </Link>
    );
}
